import React from "react";
import { createRoot } from "react-dom/client";
import { ErrorBoundary, FallbackProps } from "react-error-boundary";
import { Provider } from "react-redux";
import { Store } from "./Store";
import { SceneWrap } from "./SceneWrap";
import { SceneWrap as PreviewSceneWrap } from "PreviewSceneWrap";
import "./index.css";

declare global {
  interface Window {
    CONTENT_BASE_URL: string;
  }
}

const LOCALHOSTS = ["localhost", "127.0.0.1"];

const ProductionErrorFallback = ({ error }: FallbackProps) => {
  console.error("ProductionErrorBoundary caught an error:", error);
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="query-error">
      <div
        style={{
          color: "white",
          fontSize: "20px",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <p style={{ color: "white", marginBottom: "15px", fontSize: "24px" }}>
          <strong>Something went wrong</strong>
        </p>
        <p style={{ color: "white", marginBottom: "15px" }}>
          The image hotspot project could not be loaded.
        </p>
        <p style={{ color: "white", marginBottom: "15px" }}>
          Please check the URL or contact support.
        </p>
        <p style={{ color: "white" }}>
          <strong>Technical error:</strong> {errorMessage}
        </p>
      </div>
    </div>
  );
};

const PreviewErrorFallback = ({ error }: FallbackProps) => {
  console.error("PreviewErrorBoundary caught an error:", error);
  const errorMessage = error instanceof Error ? error.message : String(error);

  return (
    <div className="query-error">
      <div
        style={{
          color: "white",
          fontSize: "20px",
          padding: "20px",
          textAlign: "center",
        }}
      >
        <p style={{ color: "white", marginBottom: "15px", fontSize: "24px" }}>
          <strong>Project Not Found</strong>
        </p>
        <p style={{ color: "white", marginBottom: "15px" }}>
          The preview could not be loaded. Please check the project
          configuration.
        </p>
        <p style={{ color: "white" }}>
          <strong>Technical error:</strong> {errorMessage}
        </p>
      </div>
    </div>
  );
};

function getProjectName(
  segments: string[],
  isLocalhost: boolean
): string | null {
  // Local: /:project (and only one segment)
  // Prod:  /labxchange/image-hotspots/:project (and only three segments, with the first two being "labxchange" and "image-hotspots")

  if (isLocalhost && segments.length === 1) {
    return segments[0];
  }
  if (
    !isLocalhost &&
    segments.length === 3 &&
    segments[0] === "labxchange" &&
    segments[1] === "image-hotspots"
  ) {
    return segments[2];
  }
  return null;
}

function resolveProjectPath(): {
  projectName: string | null;
  isLocalhost: boolean;
  isPreview: boolean;
} {
  const segments = window.location.pathname.split("/").filter(Boolean);
  const isLocalhost = LOCALHOSTS.includes(window.location.hostname);
  const projectName = getProjectName(segments, isLocalhost);
  const isPreview = projectName === "image-hotspots-preview";

  return { projectName, isLocalhost, isPreview };
}

function AppInternal() {
  const { projectName, isLocalhost, isPreview } = resolveProjectPath();
  if (!projectName) {
    throw new Error(
      isLocalhost
        ? "Invalid URL: expected /:project"
        : "Invalid URL: expected /labxchange/image-hotspots/:project/"
    );
  }

  window.CONTENT_BASE_URL = `${import.meta.env.VITE_IINF_BASE_URL}/${projectName}/`;

  return (
    <Provider store={Store}>
      <React.Suspense
        fallback={
          <div className="fallback-loading">
            <div className="loading-animation1">.</div>
            <div className="loading-animation2">.</div>
            <div className="loading-animation3">.</div>
          </div>
        }
      >
        {isPreview ? <PreviewSceneWrap /> : <SceneWrap />}
      </React.Suspense>
    </Provider>
  );
}

function App() {
  const { projectName } = resolveProjectPath();
  const isPreview = projectName === "image-hotspots-preview";
  const ErrorFallback = isPreview
    ? PreviewErrorFallback
    : ProductionErrorFallback;

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <AppInternal />
    </ErrorBoundary>
  );
}

const container = document.getElementById("container");

if (!container) {
  throw new Error("Container element not found");
}

const root = createRoot(container);
root.render(<App />);
